"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, type RefObject } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryObserverResult } from "@tanstack/react-query";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { useSeizeSettingsOptional } from "@/contexts/SeizeSettingsContext";
import { pinnedWavesApi } from "@/services/api/pinned-waves-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWavesPinFilter } from "@/generated/models/ApiWavesPinFilter";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
export const MAX_PINNED_WAVES = 20;

// Cache time constants for React Query
const PINNED_WAVES_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const PINNED_WAVES_GC_TIME = 10 * 60 * 1000; // 10 minutes
const PINNED_WAVES_REFETCH_INTERVAL = 2 * 60 * 1000; // 2 minutes

// Type definitions for React Query data structures
interface InfiniteQueryData<T> {
  pages: T[][];
  pageParams: unknown[];
}
type PinnedWavesQueryKey = readonly [QueryKey.WAVES_OVERVIEW, {
  readonly pinned: ApiWavesPinFilter.Pinned;
  readonly viewer_identity?: string;
}];
interface MutationContext {
  previousPinnedWaves: ApiWave[] | undefined;
}
interface UsePinnedWavesServerReturn {
  pinnedWaves: ApiWave[];
  pinnedIds: string[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  pinWave: (waveId: string) => Promise<void>;
  unpinWave: (waveId: string) => Promise<void>;
  refetch: () => Promise<QueryObserverResult<ApiWave[], Error>>;
  isOperationInProgress: (waveId: string) => boolean;
  canPinWave: (waveId: string) => boolean;
}
function createPinnedWavesQueryKey(viewerIdentityKey: string | null): PinnedWavesQueryKey {
  return [QueryKey.WAVES_OVERVIEW, {
    pinned: ApiWavesPinFilter.Pinned,
    ...(viewerIdentityKey ? {
      viewer_identity: viewerIdentityKey
    } : {})
  }] as const;
}
function usePinnedWavesQueryKey(viewerIdentityKey) {
  const $ = _c(2);
  let t0;
  if ($[0] !== viewerIdentityKey) {
    t0 = createPinnedWavesQueryKey(viewerIdentityKey);
    $[0] = viewerIdentityKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function usePinnedWavesQuery(queryClient, queryKey, isAuthenticated) {
  const $ = _c(8);
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== queryKey) {
    t0 = {
      queryKey,
      queryFn: pinnedWavesApi.fetchPinnedWaves,
      enabled: isAuthenticated,
      staleTime: PINNED_WAVES_STALE_TIME,
      gcTime: PINNED_WAVES_GC_TIME,
      refetchInterval: PINNED_WAVES_REFETCH_INTERVAL,
      refetchOnWindowFocus: "always"
    };
    $[0] = isAuthenticated;
    $[1] = queryKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const query = useQuery(t0);
  let t1;
  let t2;
  if ($[3] !== isAuthenticated || $[4] !== queryClient || $[5] !== queryKey) {
    t1 = () => {
      if (!isAuthenticated) {
        queryClient.setQueryData(queryKey, []);
      }
    };
    t2 = [isAuthenticated, queryClient, queryKey];
    $[3] = isAuthenticated;
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  return query;
}
function usePinnedWavesBudget(pinnedWaves: ApiWave[], ongoingOperations: RefObject<Set<string>>) {
  const seizeSettings = useSeizeSettingsOptional();
  const pinnedIds = useMemo(() => pinnedWaves.map(wave => wave.id), [pinnedWaves]);
  const countsTowardPinBudget = useCallback((waveId: string) => !seizeSettings?.isAnnouncementsWave(waveId), [seizeSettings]);
  const pinnedBudgetCount = useMemo(() => pinnedIds.filter(countsTowardPinBudget).length, [pinnedIds, countsTowardPinBudget]);
  const getOngoingPinCount = useCallback((waveId_0: string) => {
    let ongoingPinCount = 0;
    ongoingOperations.current.forEach(id => {
      if (id === waveId_0 || pinnedIds.includes(id)) {
        return;
      }
      if (countsTowardPinBudget(id)) {
        ongoingPinCount++;
      }
    });
    return ongoingPinCount;
  }, [ongoingOperations, pinnedIds, countsTowardPinBudget]);
  const canPinWave = useCallback((waveId_1: string) => {
    if (pinnedIds.includes(waveId_1)) {
      return true;
    }
    return pinnedBudgetCount + getOngoingPinCount(waveId_1) < MAX_PINNED_WAVES;
  }, [pinnedIds, pinnedBudgetCount, getOngoingPinCount]);
  return {
    pinnedIds,
    canPinWave
  };
}
function isMainWavesQueryForViewer(queryKey: readonly unknown[], viewerIdentityKey: string | null): boolean {
  const [key, params] = queryKey;
  if (key !== QueryKey.WAVES_OVERVIEW || typeof params !== "object" || params === null) {
    return false;
  }
  const queryParams = params as {
    readonly pinned?: unknown;
    readonly viewer_identity?: unknown;
  };
  if (Boolean(queryParams.pinned)) {
    return false;
  }
  const viewerIdentityParam = queryParams.viewer_identity;
  const queryViewerIdentity = typeof viewerIdentityParam === "string" ? viewerIdentityParam : null;
  return queryViewerIdentity === viewerIdentityKey;
}
function useInvalidateWavesQueries(queryClient, pinnedWavesQueryKey, viewerIdentityKey) {
  const $ = _c(4);
  let t0;
  if ($[0] !== pinnedWavesQueryKey || $[1] !== queryClient || $[2] !== viewerIdentityKey) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: pinnedWavesQueryKey
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVES_OVERVIEW],
        predicate: query => isMainWavesQueryForViewer(query.queryKey, viewerIdentityKey)
      });
    };
    $[0] = pinnedWavesQueryKey;
    $[1] = queryClient;
    $[2] = viewerIdentityKey;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function findWaveInQueryData(data: ApiWave[] | InfiniteQueryData<ApiWave> | undefined, waveId: string): ApiWave | undefined {
  if (!data) {
    return undefined;
  }
  if (Array.isArray(data)) {
    return data.find((wave): wave is ApiWave => wave.id === waveId);
  }
  for (const page of data.pages) {
    const wave = page.find((item): item is ApiWave => item.id === waveId);
    if (wave) {
      return wave;
    }
  }
  return undefined;
}
function findWaveForOptimisticPin(queryClient: QueryClient, waveId: string): ApiWave | undefined {
  const wavesQueries = queryClient.getQueriesData<ApiWave[] | InfiniteQueryData<ApiWave>>({
    queryKey: [QueryKey.WAVES_OVERVIEW]
  });
  for (const [, data] of wavesQueries) {
    const wave = findWaveInQueryData(data, waveId);
    if (wave) {
      return wave;
    }
  }
  return undefined;
}
function createOptimisticPinnedWaves(previousPinnedWaves: ApiWave[] | undefined, waveToPin: ApiWave | undefined, waveId: string): ApiWave[] | undefined {
  if (!waveToPin || !previousPinnedWaves) {
    return undefined;
  }
  const isAlreadyPinned = previousPinnedWaves.some(wave => wave.id === waveId);
  if (isAlreadyPinned) {
    return undefined;
  }
  return [{
    ...waveToPin,
    pinned: true
  }, ...previousPinnedWaves];
}
async function optimisticallyPinWave(queryClient: QueryClient, queryKey: PinnedWavesQueryKey, waveId: string): Promise<MutationContext> {
  await queryClient.cancelQueries({
    queryKey
  });
  const previousPinnedWaves = queryClient.getQueryData<ApiWave[]>(queryKey);
  const waveToPin = findWaveForOptimisticPin(queryClient, waveId);
  const optimisticPinnedWaves = createOptimisticPinnedWaves(previousPinnedWaves, waveToPin, waveId);
  if (optimisticPinnedWaves) {
    queryClient.setQueryData(queryKey, optimisticPinnedWaves);
  }
  return {
    previousPinnedWaves
  };
}
async function optimisticallyUnpinWave(queryClient: QueryClient, queryKey: PinnedWavesQueryKey, waveId: string): Promise<MutationContext> {
  await queryClient.cancelQueries({
    queryKey
  });
  const previousPinnedWaves = queryClient.getQueryData<ApiWave[]>(queryKey);
  if (previousPinnedWaves) {
    queryClient.setQueryData(queryKey, previousPinnedWaves.filter(wave => wave.id !== waveId));
  }
  return {
    previousPinnedWaves
  };
}
function restorePinnedWaves(queryClient: QueryClient, queryKey: PinnedWavesQueryKey, context: MutationContext | undefined) {
  if (context?.previousPinnedWaves) {
    queryClient.setQueryData(queryKey, context.previousPinnedWaves);
  }
}
function usePinnedWaveMutations(queryClient, pinnedWavesQueryKey, viewerIdentityKey) {
  const $ = _c(19);
  const invalidateWavesQueries = useInvalidateWavesQueries(queryClient, pinnedWavesQueryKey, viewerIdentityKey);
  let t0;
  let t1;
  if ($[0] !== pinnedWavesQueryKey || $[1] !== queryClient) {
    t0 = waveId => optimisticallyPinWave(queryClient, pinnedWavesQueryKey, waveId);
    t1 = (err, _, context) => {
      restorePinnedWaves(queryClient, pinnedWavesQueryKey, context);
      console.error("Error pinning wave:", err);
    };
    $[0] = pinnedWavesQueryKey;
    $[1] = queryClient;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== invalidateWavesQueries || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: pinnedWavesApi.pinWave,
      onMutate: t0,
      onError: t1,
      onSuccess: invalidateWavesQueries
    };
    $[4] = invalidateWavesQueries;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const pinMutation = useMutation(t2);
  let t3;
  let t4;
  if ($[8] !== pinnedWavesQueryKey || $[9] !== queryClient) {
    t3 = waveId_0 => optimisticallyUnpinWave(queryClient, pinnedWavesQueryKey, waveId_0);
    t4 = (err_0, __0, context_0) => {
      restorePinnedWaves(queryClient, pinnedWavesQueryKey, context_0);
      console.error("Error unpinning wave:", err_0);
    };
    $[8] = pinnedWavesQueryKey;
    $[9] = queryClient;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[12] !== invalidateWavesQueries || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      mutationFn: pinnedWavesApi.unpinWave,
      onMutate: t3,
      onError: t4,
      onSuccess: invalidateWavesQueries
    };
    $[12] = invalidateWavesQueries;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const unpinMutation = useMutation(t5);
  let t6;
  if ($[16] !== pinMutation || $[17] !== unpinMutation) {
    t6 = {
      pinMutation,
      unpinMutation
    };
    $[16] = pinMutation;
    $[17] = unpinMutation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
export function usePinnedWavesServer(): UsePinnedWavesServerReturn {
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const queryClient = useQueryClient();
  const ongoingOperations = useRef<Set<string>>(new Set());
  const isAuthenticated = !!connectedProfile?.handle && !activeProfileProxy;
  const viewerIdentityKey = address?.toLowerCase() ?? null;
  const pinnedWavesQueryKey = usePinnedWavesQueryKey(viewerIdentityKey);
  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = usePinnedWavesQuery(queryClient, pinnedWavesQueryKey, isAuthenticated);
  const pinnedWaves = data ?? [];
  const {
    pinnedIds,
    canPinWave
  } = usePinnedWavesBudget(pinnedWaves, ongoingOperations);
  const {
    pinMutation,
    unpinMutation
  } = usePinnedWaveMutations(queryClient, pinnedWavesQueryKey, viewerIdentityKey);
  const pinWave = useCallback(async (waveId: string) => {
    if (ongoingOperations.current.has(waveId)) {
      throw new Error("Operation already in progress for this wave");
    }
    if (!canPinWave(waveId)) {
      throw new Error(`Maximum ${MAX_PINNED_WAVES} pinned waves allowed`);
    }
    ongoingOperations.current.add(waveId);
    try {
      await pinMutation.mutateAsync(waveId);
    } finally {
      ongoingOperations.current.delete(waveId);
    }
  }, [canPinWave, pinMutation]);
  const unpinWave = useCallback(async (waveId_0: string) => {
    if (ongoingOperations.current.has(waveId_0)) {
      throw new Error("Operation already in progress for this wave");
    }
    ongoingOperations.current.add(waveId_0);
    try {
      await unpinMutation.mutateAsync(waveId_0);
    } finally {
      ongoingOperations.current.delete(waveId_0);
    }
  }, [unpinMutation]);
  return {
    pinnedWaves,
    pinnedIds,
    isLoading,
    isError,
    error: error ?? pinMutation.error ?? unpinMutation.error,
    pinWave,
    unpinWave,
    refetch,
    isOperationInProgress: (waveId_1: string) => ongoingOperations.current.has(waveId_1),
    canPinWave
  };
}